import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { IReportEvent } from '@/hooks/useReportStream'

import { ConsoleLine } from './ConsoleLine'
import { formatEventMessage } from './formatEventMessage'

interface ConsolePanelProps {
  events: IReportEvent[]
}

interface InitLine {
  textKey: string
  delay: number
  color?: string
  prefix?: string
  showTimestamp?: boolean
}

const INIT_LINES: InitLine[] = [
  {
    textKey: 'dashboard.newSearch.generatingReport.console.init.initializing',
    delay: 500,
    color: 'text-neutralColor30',
  },
  {
    textKey: 'dashboard.newSearch.generatingReport.console.init.loadingModules',
    delay: 800,
    prefix: '▸',
    showTimestamp: true,
  },
  {
    textKey:
      'dashboard.newSearch.generatingReport.console.init.establishingConnections',
    delay: 700,
    prefix: '▸',
    showTimestamp: true,
  },
  {
    textKey: 'dashboard.newSearch.generatingReport.console.init.ready',
    delay: 500,
    prefix: '▸',
    color: 'text-dynamicGreen',
    showTimestamp: true,
  },
]

function formatEventTimestamp(timestamp: string): string {
  try {
    const d = new Date(timestamp)
    const h = String(d.getHours()).padStart(2, '0')
    const m = String(d.getMinutes()).padStart(2, '0')
    const s = String(d.getSeconds()).padStart(2, '0')
    return `${h}:${m}:${s}`
  } catch {
    return ''
  }
}

export function ConsolePanel({ events }: ConsolePanelProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const initTimestampRef = useRef(
    formatEventTimestamp(new Date().toISOString()),
  )

  const isUserInteractingRef = useRef(false)

  const [initLineIndex, setInitLineIndex] = useState(0)
  const [initComplete, setInitComplete] = useState(false)

  useEffect(() => {
    if (initComplete || initLineIndex >= INIT_LINES.length) {
      setInitComplete(true)
      return
    }

    const timer = setTimeout(() => {
      setInitLineIndex((prev) => prev + 1)
    }, INIT_LINES[initLineIndex].delay)

    return () => clearTimeout(timer)
  }, [initLineIndex, initComplete])

  useEffect(() => {
    if (!scrollRef.current || isUserInteractingRef.current) return
    const el = scrollRef.current
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [events.length, initLineIndex])

  return (
    <div className="flex flex-col h-full border-r border-neutralColor5">
      {/* Content sits at the bottom and grows upward, terminal-style. */}
      <div
        ref={scrollRef}
        onPointerEnter={() => {
          isUserInteractingRef.current = true
        }}
        onPointerLeave={() => {
          isUserInteractingRef.current = false
        }}
        className="flex-1 overflow-y-auto scrollbar-hidden"
      >
        <div className="flex flex-col justify-end min-h-full px-4 py-3 gap-1.5">
          {INIT_LINES.slice(0, initLineIndex).map((line, i) => (
            <ConsoleLine
              key={`init-${i}`}
              text={t(line.textKey)}
              prefix={line.prefix}
              color={line.color}
              timestamp={
                line.showTimestamp ? initTimestampRef.current : undefined
              }
              isTyping={i === initLineIndex - 1 && !initComplete}
            />
          ))}

          {initComplete && (
            <AnimatePresence initial={false}>
              {events.map((event, index) => (
                <motion.div
                  key={`${event.type}-${event.timestamp}-${index}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <ConsoleLine
                    text={formatEventMessage(event, t)}
                    prefix="▸"
                    timestamp={formatEventTimestamp(event.timestamp)}
                    isTyping={false}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  )
}
